import React, { useRef, useEffect, useState } from "react";
import styles from "../../css/shared/CustomDropdown.module.scss";
import sprite from "../../assets/icons/modals-complete.svg";
import { isEmptyArray, isEmptyVal } from "../../helpers/utils_types";
import { PropTypes } from "prop-types";
import { useOutsideClick } from "../../utils/useOutsideClick";
import { useKeyboardShortcut } from "../../utils/useKeyboardShortcut";
import { blueGrey, red } from "../../helpers/utils_styles";

const getIconColor = (selection) => {
	if (isEmptyVal(selection)) {
		return {
			fill: blueGrey[700],
		};
	} else {
		return {
			fill: red[600],
		};
	}
};

const ENTER = "Enter" || 13;
const ESCAPE = "Escape" || 27;

const OptionsMenu = ({
	name,
	closeHandler,
	handleSelection,
	handleSelectionByKey,
	handleFocus,
	hasFocus,
	options = [],
}) => {
	const menuRef = useRef();
	const { isOutside } = useOutsideClick(menuRef);
	const wasEscaped = useKeyboardShortcut(["Escape"]);

	useEffect(() => {
		let isMounted = true;
		if (!isMounted) {
			return;
		}
		if (isOutside || wasEscaped) {
			return closeHandler(false);
		}
		return () => {
			isMounted = false;
		};
	}, [isOutside, wasEscaped, closeHandler]);

	return (
		<aside className={styles.CustomDropdown_options} ref={menuRef}>
			<ul className={styles.CustomDropdown_options_list}>
				{!isEmptyArray(options) &&
					options.map((option, index) => (
						<li
							tabIndex={0}
							key={`${option}__${index}`}
							onClick={() => handleSelection(name, option)}
							onFocus={() => handleFocus(option)}
							onKeyDown={handleSelectionByKey}
							className={
								hasFocus === option
									? styles.CustomDropdown_options_list_item_focus
									: styles.CustomDropdown_options_list_item
							}
						>
							{option}
						</li>
					))}
			</ul>
		</aside>
	);
};

const CustomDropdown = ({
	name,
	id,
	label,
	options = [],
	placeholder,
	selection,
	setSelection,
	autoFocus = false,
	isDisabled = false,
	isRequired = false,
	inputMode = "text",
	customStyles = {},
}) => {
	const inputRef = useRef();
	const [showOptions, setShowOptions] = useState(false); // menu options
	const [listOptions, setListOptions] = useState(options); // for filtering/searching
	const [hasFocus, setHasFocus] = useState("");
	const [searchVal, setSearchVal] = useState("");

	// handles "click" selection for a menu option
	const handleSelection = (name, option) => {
		setSelection(name, option);
	};

	// sets active focused item
	// allows custom focus styles to be applied
	const handleFocus = (option) => {
		setHasFocus(option);
	};

	// handles checking for a match
	// setting the selection when an option is focused and the
	// "ENTER" key is pressed
	const handleSelectionByKey = (e) => {
		const currentEl = e.target.textContent;

		const hasMatch = (val) => {
			return listOptions.includes(val);
		};
		if (hasMatch(currentEl) && e.key === ENTER) {
			return handleSelection(name, currentEl);
		}
		if (e.key === ESCAPE) {
			return setShowOptions(false);
		}
		return;
	};

	// handles "onChange" search
	// if 'selection' exists, then clear it and continue w/ 'handleSearch', as normal
	const handleSearch = (e) => {
		const { value } = e.target;
		if (!isEmptyVal(selection)) {
			clearSelection();
			setSearchVal(value);
			return setListOptions([
				...options.filter((x) => x.toLowerCase().includes(value.toLowerCase())),
			]);
		} else {
			setSearchVal(value);
			setListOptions([
				...options.filter((x) => x.toLowerCase().includes(value.toLowerCase())),
			]);
		}
	};

	// clear search, focus, 'selection' & reset list 'options'
	const clearSelection = () => {
		handleSelection(name, "");
		setSearchVal("");
		handleFocus("");
		setListOptions([...options]);
	};

	useEffect(() => {
		let isMounted = true;
		if (!isMounted) {
			return;
		}
		// close options menu when selection is made
		if (!isEmptyVal(selection)) {
			return setShowOptions(false);
		}

		return () => {
			isMounted = false;
		};
	}, [selection]);

	return (
		<div className={styles.CustomDropdown} style={customStyles}>
			<label htmlFor={id} className={styles.CustomDropdown_label}>
				{label}
			</label>
			<div className={styles.CustomDropdown_inputWrapper} style={customStyles}>
				<input
					ref={inputRef}
					type="text"
					value={isEmptyVal(selection) ? searchVal : selection}
					name={name}
					id={id}
					placeholder={placeholder}
					onChange={handleSearch}
					onClick={isDisabled ? null : () => setShowOptions(true)}
					onFocus={isDisabled ? null : () => setShowOptions(true)}
					className={styles.CustomDropdown_inputWrapper_input}
					style={customStyles}
					autoComplete="off"
					autoFocus={autoFocus}
					disabled={isDisabled}
					inputMode={inputMode}
					required={isRequired}
				/>
				{showOptions && (
					<OptionsMenu
						name={name}
						handleSelection={handleSelection}
						handleSelectionByKey={handleSelectionByKey}
						handleFocus={handleFocus}
						hasFocus={hasFocus}
						closeHandler={setShowOptions}
						options={listOptions}
					/>
				)}

				<svg
					className={styles.CustomDropdown_closeIcon}
					onClick={() => {
						if (isDisabled) return;
						if (isEmptyVal(selection)) {
							return setShowOptions(true);
						} else {
							return clearSelection(name);
						}
					}}
					style={getIconColor(selection)}
				>
					<use
						xlinkHref={`${sprite}#icon-${
							!isEmptyVal(selection) ? "clearclose" : "caret-down"
						}`}
					/>
				</svg>
			</div>
		</div>
	);
};

export default CustomDropdown;

CustomDropdown.defaultProps = {
	options: [],
};

CustomDropdown.propTypes = {
	name: PropTypes.string,
	id: PropTypes.string,
	label: PropTypes.string,
	inputMode: PropTypes.string,
	options: PropTypes.array,
	selection: PropTypes.string.isRequired, // input state value
	setSelection: PropTypes.func.isRequired, // state setter for input
};

OptionsMenu.defaultProps = {
	options: [],
};
OptionsMenu.defaultProps = {
	name: PropTypes.string.isRequired,
	closeHandler: PropTypes.func.isRequired,
	handleSelection: PropTypes.func.isRequired, // "onClick" handler for value selection
	options: PropTypes.array.isRequired, // menu options
};
